import { Component } from '@theme/component'
import { createGsapUnderlineController } from '@theme/gsap-draw-underline'
import { loadGsapDraw } from '@theme/gsap-runtime'

const imageWithSliderConfig = {
  activeClass: 'is-active',
  enteringClass: 'is-entering',
  returningClass: 'is-returning',
  selectors: {
    media: '[data-image-with-slider-media]',
    trigger: '[data-draw-underline]'
  },
  transitionFallbackMs: 850
}

class ImageWithSlider extends Component {
  #activeMediaIndex = null
  #incomingMediaIndex = null
  #mediaTransitionTimer = null
  #triggers = []
  #underlineController = null

  connectedCallback() {
    super.connectedCallback()
    this.#underlineController = createGsapUnderlineController({
      isConnected: () => this.isConnected,
      loadGsapDraw
    })
    this.bindTriggers()
    this.activateMedia(0)
  }

  disconnectedCallback() {
    this.clearMediaTransitionTimer()
    this.unbindTriggers()
    super.disconnectedCallback()
  }

  activateDrawUnderline = (event) => {
    const trigger = this.getTriggerFromEvent(event)
    if (!trigger) return

    this.activateMedia(this.#triggers.indexOf(trigger))
    this.#underlineController.draw(trigger)
  }

  eraseDrawUnderline = (event) => {
    const trigger = this.getTriggerFromEvent(event)
    if (!trigger) return

    this.#underlineController.erase(trigger)
  }

  bindTriggers() {
    this.unbindTriggers()
    this.#triggers = this.getTriggers()

    for (const trigger of this.#triggers) {
      trigger.addEventListener('mouseenter', this.activateDrawUnderline)
      trigger.addEventListener('mouseleave', this.eraseDrawUnderline)
      trigger.addEventListener('focusin', this.activateDrawUnderline)
      trigger.addEventListener('focusout', this.eraseDrawUnderline)
    }
  }

  unbindTriggers() {
    for (const trigger of this.#triggers) {
      trigger.removeEventListener('mouseenter', this.activateDrawUnderline)
      trigger.removeEventListener('mouseleave', this.eraseDrawUnderline)
      trigger.removeEventListener('focusin', this.activateDrawUnderline)
      trigger.removeEventListener('focusout', this.eraseDrawUnderline)
    }

    this.#triggers = []
  }

  getTriggers() {
    return Array.from(
      this.querySelectorAll(imageWithSliderConfig.selectors.trigger)
    ).filter((trigger) => trigger instanceof HTMLButtonElement)
  }

  getMediaItems() {
    return Array.from(
      this.querySelectorAll(imageWithSliderConfig.selectors.media)
    ).filter((media) => media instanceof HTMLElement)
  }

  getTriggerFromEvent(event) {
    return event.currentTarget instanceof HTMLButtonElement
      ? event.currentTarget
      : null
  }

  activateMedia(activeIndex) {
    const mediaItems = this.getMediaItems()
    if (activeIndex < 0 || activeIndex >= mediaItems.length) return
    if (activeIndex === this.#incomingMediaIndex) return
    if (this.#activeMediaIndex === null) {
      this.setBaseMedia(mediaItems, activeIndex)
      return
    }

    if (activeIndex === this.#activeMediaIndex) {
      this.returnIncomingMedia(mediaItems)
      return
    }

    this.startMediaTransition(mediaItems, activeIndex)
  }

  setBaseMedia(mediaItems, activeIndex) {
    this.clearMediaTransitionTimer()
    this.#activeMediaIndex = activeIndex
    this.#incomingMediaIndex = null

    for (const [index, media] of mediaItems.entries()) {
      const isActive = index === activeIndex

      media.classList.toggle(imageWithSliderConfig.activeClass, isActive)
      media.classList.remove(
        imageWithSliderConfig.enteringClass,
        imageWithSliderConfig.returningClass
      )
      media.toggleAttribute('aria-hidden', !isActive)
    }
  }

  startMediaTransition(mediaItems, activeIndex) {
    this.returnIncomingMedia(mediaItems)

    const activeMedia = mediaItems[activeIndex]
    activeMedia.classList.remove(imageWithSliderConfig.returningClass)
    activeMedia.toggleAttribute('aria-hidden', false)
    activeMedia.getBoundingClientRect()
    activeMedia.classList.add(imageWithSliderConfig.enteringClass)

    this.#incomingMediaIndex = activeIndex
    this.queueMediaTransitionEnd(mediaItems, activeIndex)
  }

  returnIncomingMedia(mediaItems) {
    this.clearMediaTransitionTimer()
    this.#incomingMediaIndex = null

    for (const media of mediaItems) {
      if (!media.classList.contains(imageWithSliderConfig.enteringClass)) {
        continue
      }

      media.classList.remove(imageWithSliderConfig.enteringClass)
      media.classList.add(imageWithSliderConfig.returningClass)
      media.toggleAttribute('aria-hidden', true)
      this.queueReturningMediaCleanup(media)
    }
  }

  queueMediaTransitionEnd(mediaItems, activeIndex) {
    this.#mediaTransitionTimer = window.setTimeout(() => {
      this.setBaseMedia(mediaItems, activeIndex)
    }, imageWithSliderConfig.transitionFallbackMs)
  }

  queueReturningMediaCleanup(media) {
    window.setTimeout(() => {
      media.classList.remove(imageWithSliderConfig.returningClass)
    }, imageWithSliderConfig.transitionFallbackMs)
  }

  clearMediaTransitionTimer() {
    if (!this.#mediaTransitionTimer) return

    window.clearTimeout(this.#mediaTransitionTimer)
    this.#mediaTransitionTimer = null
  }
}

if (!customElements.get('image-with-slider')) {
  customElements.define('image-with-slider', ImageWithSlider)
}
